// 🔴 N°1016 — la liste des VISITES d'un dossier dans la fiche OmniHub.
//
// Constat PROD (08/10) : « Dans OH je ne vois aucune visite ». Les visites de Visite Rénovation
// sont des RELEVÉS (`chantier_releves`) ; la fiche n'affichait qu'un bloc « Relevés du dossier »
// sans adresse ni action claire. Cette liste lit la MÊME donnée que l'écran terrain (route
// serveur unique) — ⛔ jamais `visite_saisies`, ⛔ aucune copie : une visite n'a qu'une vérité.
//
//   EN COURS            ⇒ Reprendre
//   CLOSE sans sortie   ⇒ Consulter + Rouvrir pour compléter
//   CLOSE avec devis    ⇒ Consulter uniquement
//   ABANDONNÉE          ⇒ Consulter (historique)
// ⛔ Le serveur dit ce qui est permis (`rouvrable`) ; l'écran ne rejoue pas la règle.
import { useEffect, useState } from "react";
import { rouvrirVisite, visitesDuDossier } from "./api";

const quand = (t) => (t ? new Date(t).toLocaleString("fr-FR") : "—");
const ETATS = { en_cours: "En cours", clos: "Clôturée", abandonne: "Abandonnée" };
const BTN = { borderRadius: 8, height: 32, padding: "0 12px", fontWeight: 500, cursor: "pointer" };
const PRIMAIRE = { ...BTN, background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)", border: "none" };
const SECONDAIRE = { ...BTN, background: "transparent", color: "var(--oh-accent-text)", border: "0.5px solid var(--oh-accent)" };

export function ListeVisitesDossier({ dossierId, rev = 0, onReprendre, onConsulter, onRouvert }) {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState("");
  const [occupe, setOccupe] = useState("");

  useEffect(() => {
    let vivant = true;
    setErreur("");
    visitesDuDossier(dossierId)
      .then((d) => vivant && setDonnees(d))
      .catch((e) => vivant && (setDonnees({ visites: [], dossier: {} }), setErreur(e.message)));
    return () => { vivant = false; };
  }, [dossierId, rev]);

  const rouvrir = async (v) => {
    setErreur(""); setOccupe(v.id);
    try { await rouvrirVisite(v.id); onRouvert && onRouvert(v); }
    catch (e) { setErreur(e.message); } finally { setOccupe(""); }
  };

  if (!donnees) return <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Chargement des visites…</p>;
  const lieu = donnees.dossier?.lieu;
  return (
    <section data-testid="oh-visites" style={{ display: "grid", gap: 8 }}>
      {erreur && <p role="alert" className="text-sm" data-testid="oh-visites-erreur" style={{ color: "var(--oh-error-text)" }}>{erreur}</p>}
      {donnees.visites.length === 0 && !erreur && (
        <p className="text-sm" data-testid="oh-visites-vide" style={{ color: "var(--oh-text-muted)" }}>Aucune visite pour ce dossier.</p>
      )}
      {donnees.visites.map((v) => (
        <div key={v.id} data-testid={`oh-visite-${v.id}`}
             style={{ display: "grid", gap: 6, padding: 12, borderRadius: 8, border: "0.5px solid var(--oh-border)",
                      background: "var(--oh-bg)" }}>
          <div className="text-sm" style={{ color: "var(--oh-text)" }}>
            <strong data-testid={`oh-visite-etat-${v.id}`} style={{ fontWeight: 500 }}>{ETATS[v.etat] || v.etat}</strong>
            {" · "}<span data-testid={`oh-visite-lieu-${v.id}`}>{lieu || v.lieu || "adresse non renseignée"}</span>
            {" · "}{v.etat === "en_cours" ? `ouverte le ${quand(v.ouvert_le)}` : `le ${quand(v.clos_le || v.ouvert_le)}`}
            {" · "}{v.nombre_postes || 0} ligne(s){v.devis_numero ? ` · devis ${v.devis_numero}` : ""}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {v.etat === "en_cours" && (
              <button type="button" data-testid={`oh-visite-reprendre-${v.id}`} style={PRIMAIRE}
                      onClick={() => onReprendre && onReprendre(v)}>Reprendre</button>
            )}
            {v.etat !== "en_cours" && (
              <button type="button" data-testid={`oh-visite-consulter-${v.id}`} style={SECONDAIRE}
                      onClick={() => onConsulter && onConsulter(v)}>Consulter</button>
            )}
            {v.rouvrable && (
              <button type="button" data-testid={`oh-visite-rouvrir-${v.id}`} style={PRIMAIRE} disabled={occupe === v.id}
                      onClick={() => rouvrir(v)}>{occupe === v.id ? "Réouverture…" : "Rouvrir pour compléter"}</button>
            )}
          </div>
        </div>
      ))}
    </section>
  );
}

export default ListeVisitesDossier;
